import { Controller } from "@hotwired/stimulus"

const escapeHtml = (value) => {
  if (value == null) return ""
  const div = document.createElement("div")
  div.textContent = String(value)
  return div.innerHTML
}

// Shift Swap Controller
// Manages the multi-step swap modal using Turbo Frames for content loading,
// plus day detail and shift detail modals for the staff calendar view.
export default class extends Controller {
  static targets = [
    "modal", "slotInfo", "membersList", "loading", "error", "success",
    "backButton", "stepIndicator", "messageInput", "sendButton", "shiftOption",
    "dayModal", "dayModalTitle", "dayModalContent",
    "shiftModal", "shiftModalTitle", "shiftModalDate", "shiftModalContent",
    "shiftModalTime", "shiftModalDuration", "shiftModalCoverageCount", "shiftModalCoveragePercent",
    "shiftModalCoverageBar", "shiftModalMinStaff", "shiftModalAssignedCount", "shiftModalStaffList",
    "shiftModalNoStaff", "shiftModalWarnings", "shiftModalWarningText"
  ]

  static values = {
    selectShiftMessage: { type: String, default: "Please select a shift to swap" },
    sendingLabel: { type: String, default: "Sending..." },
    failedMessage: { type: String, default: "Failed to send swap request" },
    hoursLabel: { type: String, default: "hours" },
    minimumStaffLabel: { type: String, default: "Minimum Staff" },
    needMoreStaff: { type: String, default: "Need %{count} more staff member(s)" }
  }

  connect() {
    this.boundHandleEscape = this.handleEscape.bind(this)
    document.addEventListener("keydown", this.boundHandleEscape)
    this.currentStep = 1
    this.currentSlotId = null
    this.selectedRecipientSlotId = null
  }

  disconnect() {
    document.removeEventListener("keydown", this.boundHandleEscape)
  }

  // ── Swap Modal (Turbo Frame based) ──────────────────────────────

  openSwapModal(event) {
    event.preventDefault()
    const el = event.currentTarget
    this.currentSlotId = el.dataset.slotId
    this.currentStep = 1
    this.updateStepIndicator()
    this.hideBackButton()
    this.showModal()
    this.showLoading()
    this.updateSlotInfo(el.dataset.slotDate, el.dataset.shiftName, el.dataset.shiftTime)

    // Load swap options via Turbo Frame
    const frame = this.membersListTarget
    frame.src = `/staff/calendar/swap_options?slot_id=${this.currentSlotId}`
    frame.addEventListener("turbo:frame-load", () => this.hideLoading(), { once: true })
  }

  selectRecipient(event) {
    event.preventDefault()
    const button = event.currentTarget
    this.selectedRecipientId = button.dataset.recipientId
    this.selectedRecipientName = button.dataset.recipientName

    this.currentStep = 2
    this.updateStepIndicator()
    this.showBackButton()
    this.showLoading()

    // Load recipient shifts via Turbo Frame
    const frame = this.membersListTarget
    frame.src = `/staff/calendar/recipient_shifts?slot_id=${this.currentSlotId}&recipient_id=${this.selectedRecipientId}`
    frame.addEventListener("turbo:frame-load", () => this.hideLoading(), { once: true })
  }

  goBack(event) {
    event.preventDefault()
    if (this.currentStep === 2) {
      this.currentStep = 1
      this.updateStepIndicator()
      this.hideBackButton()
      this.showLoading()

      const frame = this.membersListTarget
      frame.src = `/staff/calendar/swap_options?slot_id=${this.currentSlotId}`
      frame.addEventListener("turbo:frame-load", () => this.hideLoading(), { once: true })
    }
  }

  selectShift(event) {
    this.selectedRecipientSlotId = event.currentTarget.dataset.recipientSlotId
    if (this.hasShiftOptionTarget) {
      this.shiftOptionTargets.forEach(option => {
        const isSelected = option.dataset.recipientSlotId === this.selectedRecipientSlotId
        option.classList.toggle("border-teal-500", isSelected)
        option.classList.toggle("bg-teal-50", isSelected)
        option.classList.toggle("border-gray-200", !isSelected)
      })
    }
    if (this.hasSendButtonTarget) this.sendButtonTarget.disabled = false
  }

  async sendSwapRequest(event) {
    event.preventDefault()
    if (!this.selectedRecipientSlotId) {
      this.showError(this.selectShiftMessageValue)
      return
    }

    const button = event.currentTarget
    button.disabled = true
    const originalHTML = button.innerHTML
    button.innerHTML = `<i class="fas fa-spinner fa-spin mr-2"></i> ${this.sendingLabelValue}`

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    const params = new URLSearchParams({
      slot_id: this.currentSlotId,
      recipient_id: this.selectedRecipientId,
      recipient_slot_id: this.selectedRecipientSlotId
    })
    const message = this.hasMessageInputTarget ? this.messageInputTarget.value.trim() : ""
    if (message) params.append("message", message)

    try {
      const response = await fetch("/staff/calendar/request_swap", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "X-CSRF-Token": csrfToken,
          "Accept": "text/vnd.turbo-stream.html, text/html"
        },
        body: params.toString()
      })
      const streamHtml = await response.text()
      // Let the server drive the modal: it returns a turbo-stream that renders
      // either the "sent" confirmation or the specific error into
      // #swap_modal_content. Never optimistically show success — a rejected
      // swap comes back as 422 and its error stream must win.
      if (window.Turbo && streamHtml.includes("turbo-stream")) {
        window.Turbo.renderStreamMessage(streamHtml)
      } else if (response.ok) {
        this.showSuccess(this.selectedRecipientName)
      } else {
        button.disabled = false
        button.innerHTML = originalHTML
        this.showError(this.failedMessageValue)
      }
    } catch (error) {
      button.disabled = false
      button.innerHTML = originalHTML
      this.showError(error.message)
    }
  }

  // ── Swap Modal UI helpers ───────────────────────────────────────

  updateSlotInfo(date, name, time) {
    if (!this.hasSlotInfoTarget || !date) return
    const d = new Date(date)
    this.slotInfoTarget.innerHTML = `
      <div class="flex items-center space-x-3 mb-4 pb-4 border-b border-gray-200">
        <div class="w-12 h-12 bg-teal-100 rounded-lg flex flex-col items-center justify-center">
          <span class="text-xs font-medium text-teal-600">${escapeHtml(d.toLocaleDateString(document.documentElement.lang || "nl", { month: "short" }))}</span>
          <span class="text-sm font-bold text-teal-700">${d.getDate()}</span>
        </div>
        <div>
          <p class="font-semibold text-gray-900">${escapeHtml(name)}</p>
          <p class="text-sm text-gray-500">${escapeHtml(d.toLocaleDateString(document.documentElement.lang || "nl", { weekday: "long" }))} &bull; ${escapeHtml(time)}</p>
        </div>
      </div>`
  }

  updateStepIndicator() {
    if (!this.hasStepIndicatorTarget) return
    const step1 = this.stepIndicatorTarget.querySelector('[data-step="1"]')
    const step2 = this.stepIndicatorTarget.querySelector('[data-step="2"]')
    if (!step1 || !step2) return

    step1.className = "w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold bg-teal-600 text-white"
    step2.className = this.currentStep >= 2
      ? "w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold bg-teal-600 text-white"
      : "w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold bg-gray-200 text-gray-600"
  }

  showBackButton() { if (this.hasBackButtonTarget) this.backButtonTarget.classList.remove("hidden") }
  hideBackButton() { if (this.hasBackButtonTarget) this.backButtonTarget.classList.add("hidden") }

  showModal() {
    if (this.hasModalTarget) {
      this.modalTarget.classList.remove("hidden")
      this.modalTarget.classList.add("flex")
      document.body.style.overflow = "hidden"
    }
  }

  closeModal() {
    if (this.hasModalTarget) {
      this.modalTarget.classList.add("hidden")
      this.modalTarget.classList.remove("flex")
      document.body.style.overflow = ""
    }
    this.hideError()
    this.hideSuccess()
    if (this.hasMembersListTarget) this.membersListTarget.innerHTML = ""
    if (this.hasMessageInputTarget) this.messageInputTarget.value = ""
    this.selectedRecipientSlotId = null
  }

  closeOnBackdrop(event) { if (event.target === event.currentTarget) this.closeModal() }
  stopPropagation(event) { event.stopPropagation() }

  showLoading() {
    if (this.hasLoadingTarget) this.loadingTarget.classList.remove("hidden")
    if (this.hasMembersListTarget) this.membersListTarget.classList.add("hidden")
  }

  hideLoading() {
    if (this.hasLoadingTarget) this.loadingTarget.classList.add("hidden")
    if (this.hasMembersListTarget) this.membersListTarget.classList.remove("hidden")
  }

  showError(message) {
    if (this.hasErrorTarget) {
      this.errorTarget.classList.remove("hidden")
      const span = this.errorTarget.querySelector("span")
      if (span) span.textContent = message
    }
  }

  hideError() { if (this.hasErrorTarget) this.errorTarget.classList.add("hidden") }

  showSuccess(recipientName) {
    if (this.hasMembersListTarget) this.membersListTarget.classList.add("hidden")
    if (this.hasSuccessTarget) {
      this.successTarget.classList.remove("hidden")
      const nameSpan = this.successTarget.querySelector("[data-recipient-name]")
      if (nameSpan) nameSpan.textContent = recipientName
    }
  }

  hideSuccess() { if (this.hasSuccessTarget) this.successTarget.classList.add("hidden") }

  // ── Escape key handling ─────────────────────────────────────────

  handleEscape(event) {
    if (event.key !== "Escape") return
    if (this.hasShiftModalTarget && !this.shiftModalTarget.classList.contains("hidden")) {
      this.closeShiftModal()
    } else if (this.hasDayModalTarget && !this.dayModalTarget.classList.contains("hidden")) {
      this.closeDayModal()
    } else if (this.hasModalTarget && !this.modalTarget.classList.contains("hidden")) {
      this.closeModal()
    }
  }

  // ── Day Detail Modal ────────────────────────────────────────────

  openDayModal(event) {
    event.preventDefault()
    const button = event.currentTarget
    const dateStr = button.dataset.date
    if (this.hasDayModalTitleTarget) this.dayModalTitleTarget.textContent = button.dataset.formattedDate
    const shiftsContainer = document.getElementById(`day-shifts-${dateStr}`)
    if (shiftsContainer && this.hasDayModalContentTarget) {
      this.dayModalContentTarget.innerHTML = shiftsContainer.innerHTML
    }
    if (this.hasDayModalTarget) {
      this.dayModalTarget.classList.remove("hidden")
      this.dayModalTarget.classList.add("flex")
      document.body.style.overflow = "hidden"
    }
  }

  closeDayModal() {
    if (this.hasDayModalTarget) {
      this.dayModalTarget.classList.add("hidden")
      this.dayModalTarget.classList.remove("flex")
      document.body.style.overflow = ""
    }
  }

  closeOnDayModalBackdrop(event) { if (event.target === this.dayModalTarget) this.closeDayModal() }

  // ── Shift Detail Modal ──────────────────────────────────────────

  openShiftModal(event) {
    event.preventDefault()
    event.stopPropagation()
    const el = event.currentTarget
    const assigned = parseInt(el.dataset.shiftAssigned) || 0
    const required = parseInt(el.dataset.shiftRequired) || 0
    const coverage = parseInt(el.dataset.shiftCoverage) || 0
    const openCount = parseInt(el.dataset.shiftOpen) || 0
    let staffList = []
    try { staffList = JSON.parse(el.dataset.shiftStaff || "[]") } catch (e) { /* ignore */ }

    if (this.hasShiftModalTitleTarget) this.shiftModalTitleTarget.textContent = el.dataset.shiftName || "Shift"
    if (this.hasShiftModalDateTarget) this.shiftModalDateTarget.textContent = el.dataset.shiftFormattedDate || ""
    if (this.hasShiftModalTimeTarget) this.shiftModalTimeTarget.textContent = el.dataset.shiftTime || "-"
    if (this.hasShiftModalDurationTarget) this.shiftModalDurationTarget.textContent = `${el.dataset.shiftDuration || "-"} ${this.hoursLabelValue}`
    if (this.hasShiftModalCoverageCountTarget) this.shiftModalCoverageCountTarget.textContent = `${assigned}/${required}`
    if (this.hasShiftModalCoveragePercentTarget) this.shiftModalCoveragePercentTarget.textContent = `${coverage}%`

    if (this.hasShiftModalCoverageBarTarget) {
      this.shiftModalCoverageBarTarget.style.width = `${Math.min(coverage, 100)}%`
      this.shiftModalCoverageBarTarget.style.backgroundColor = coverage >= 100 ? "#22c55e" : coverage >= 50 ? "#eab308" : "#ef4444"
    }

    if (this.hasShiftModalMinStaffTarget) {
      const isUnder = assigned < required
      this.shiftModalMinStaffTarget.innerHTML = `<i class="fas fa-${isUnder ? "times text-red-500" : "check text-green-500"} mr-2"></i>${this.minimumStaffLabelValue}: ${required}`
      this.shiftModalMinStaffTarget.className = `inline-flex items-center px-3 py-1 rounded-full text-sm border ${isUnder ? "border-red-200 bg-red-50 text-red-700" : "border-green-200 bg-green-50 text-green-700"}`
    }

    if (this.hasShiftModalAssignedCountTarget) this.shiftModalAssignedCountTarget.textContent = assigned

    if (this.hasShiftModalStaffListTarget) {
      if (staffList.length > 0) {
        this.shiftModalStaffListTarget.innerHTML = staffList.map(s => `
          <div class="flex items-center p-3 bg-gray-50 rounded-lg border">
            <div class="w-10 h-10 rounded-full bg-teal-500 flex items-center justify-center text-white font-medium mr-3">${escapeHtml(s.initials)}</div>
            <div><p class="font-medium text-gray-900">${escapeHtml(s.name)}</p><p class="text-sm text-gray-500">${escapeHtml(s.role)}</p></div>
          </div>`).join("")
        this.shiftModalStaffListTarget.classList.remove("hidden")
        if (this.hasShiftModalNoStaffTarget) this.shiftModalNoStaffTarget.classList.add("hidden")
      } else {
        this.shiftModalStaffListTarget.classList.add("hidden")
        if (this.hasShiftModalNoStaffTarget) this.shiftModalNoStaffTarget.classList.remove("hidden")
      }
    }

    if (this.hasShiftModalWarningsTarget) {
      if (openCount > 0) {
        this.shiftModalWarningsTarget.classList.remove("hidden")
        if (this.hasShiftModalWarningTextTarget) this.shiftModalWarningTextTarget.textContent = this.needMoreStaffValue.replace("%{count}", openCount)
      } else {
        this.shiftModalWarningsTarget.classList.add("hidden")
      }
    }

    if (this.hasShiftModalTarget) {
      this.shiftModalTarget.classList.remove("hidden")
      this.shiftModalTarget.classList.add("flex")
      document.body.style.overflow = "hidden"
    }
  }

  closeShiftModal() {
    if (this.hasShiftModalTarget) {
      this.shiftModalTarget.classList.add("hidden")
      this.shiftModalTarget.classList.remove("flex")
      document.body.style.overflow = ""
    }
  }

  closeOnShiftModalBackdrop(event) { if (event.target === this.shiftModalTarget) this.closeShiftModal() }
}
